import React, { useEffect, useRef, useState, useCallback } from 'react';
import planchetteIntelService from '../../services/planchetteIntelService';

const NODE_COLORS = {
  person: '#3B82F6',       // blue
  organization: '#8B5CF6', // purple
  property: '#10B981',     // green
  court_case: '#F59E0B',   // orange
  vehicle: '#6B7280',      // gray
  aircraft: '#94A3B8',     // steel
};

const NODE_RADIUS = {
  0: 20,  // root
  1: 16,
  2: 12,
  3: 10,
};

const GraphVisualization = ({ entityId }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const nodesRef = useRef([]);
  const edgesRef = useRef([]);
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [depth, setDepth] = useState(2);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [dragNode, setDragNode] = useState(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const loadGraph = useCallback(async () => {
    try {
      setLoading(true);
      const data = await planchetteIntelService.graphVisualization(entityId, depth);
      setGraphData(data);
    } catch (err) {
      console.error('Failed to load graph:', err);
    } finally {
      setLoading(false);
    }
  }, [entityId, depth]);

  useEffect(() => {
    loadGraph();
  }, [loadGraph]);

  // Initialize force simulation
  useEffect(() => {
    if (!graphData || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.parentElement.clientWidth;
    const height = canvas.parentElement.clientHeight || 600;
    canvas.width = width;
    canvas.height = height;

    const centerX = width / 2;
    const centerY = height / 2;

    // Initialize nodes with positions
    const nodes = graphData.nodes.map((n, i) => ({
      ...n,
      x: centerX + (Math.random() - 0.5) * 200,
      y: centerY + (Math.random() - 0.5) * 200,
      vx: 0,
      vy: 0,
      radius: NODE_RADIUS[n.depth] || 8,
    }));

    // Put root at center
    if (nodes.length > 0) {
      nodes[0].x = centerX;
      nodes[0].y = centerY;
    }

    const nodeMap = {};
    nodes.forEach(n => { nodeMap[n.id] = n; });

    const edges = graphData.edges.map(e => ({
      ...e,
      sourceNode: nodeMap[e.source],
      targetNode: nodeMap[e.target],
    })).filter(e => e.sourceNode && e.targetNode);

    nodesRef.current = nodes;
    edgesRef.current = edges;

    // Force simulation parameters
    const REPULSION = 5000;
    const ATTRACTION = 0.01;
    const DAMPING = 0.85;
    const IDEAL_LENGTH = 120;

    function simulate() {
      // Repulsion between all nodes
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[j].x - nodes[i].x;
          const dy = nodes[j].y - nodes[i].y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const force = REPULSION / (dist * dist);
          const fx = (dx / dist) * force;
          const fy = (dy / dist) * force;
          nodes[i].vx -= fx;
          nodes[i].vy -= fy;
          nodes[j].vx += fx;
          nodes[j].vy += fy;
        }
      }

      // Attraction along edges
      for (const edge of edges) {
        const dx = edge.targetNode.x - edge.sourceNode.x;
        const dy = edge.targetNode.y - edge.sourceNode.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const force = (dist - IDEAL_LENGTH) * ATTRACTION;
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;
        edge.sourceNode.vx += fx;
        edge.sourceNode.vy += fy;
        edge.targetNode.vx -= fx;
        edge.targetNode.vy -= fy;
      }

      // Center gravity
      for (const node of nodes) {
        node.vx += (centerX - node.x) * 0.0005;
        node.vy += (centerY - node.y) * 0.0005;
      }

      // Apply velocities with damping
      for (const node of nodes) {
        if (node === dragNode) continue;
        node.vx *= DAMPING;
        node.vy *= DAMPING;
        node.x += node.vx;
        node.y += node.vy;
        // Keep in bounds
        node.x = Math.max(node.radius, Math.min(width - node.radius, node.x));
        node.y = Math.max(node.radius, Math.min(height - node.radius, node.y));
      }
    }

    function draw() {
      ctx.clearRect(0, 0, width, height);

      // Draw edges
      for (const edge of edges) {
        ctx.beginPath();
        ctx.moveTo(edge.sourceNode.x, edge.sourceNode.y);
        ctx.lineTo(edge.targetNode.x, edge.targetNode.y);
        ctx.strokeStyle = edge.current !== false ? 'rgba(148, 163, 184, 0.3)' : 'rgba(148, 163, 184, 0.1)';
        ctx.lineWidth = edge.current !== false ? 1.5 : 1;
        ctx.stroke();

        // Edge label
        const midX = (edge.sourceNode.x + edge.targetNode.x) / 2;
        const midY = (edge.sourceNode.y + edge.targetNode.y) / 2;
        ctx.font = '9px sans-serif';
        ctx.fillStyle = 'rgba(148, 163, 184, 0.5)';
        ctx.textAlign = 'center';
        ctx.fillText(edge.rel_type.replace(/_/g, ' '), midX, midY - 4);
      }

      // Draw nodes
      for (const node of nodes) {
        const isHovered = hoveredNode === node.id;
        const isRoot = node.depth === 0;

        // Glow for root
        if (isRoot) {
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.radius + 4, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(250, 204, 21, 0.15)';
          ctx.fill();
        }

        // Node circle
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fillStyle = NODE_COLORS[node.entity_type] || '#6B7280';
        ctx.fill();

        if (isHovered) {
          ctx.strokeStyle = '#FACC15';
          ctx.lineWidth = 2;
          ctx.stroke();
        }

        // Label
        ctx.font = `${isRoot ? 'bold ' : ''}${isRoot ? 12 : 10}px sans-serif`;
        ctx.fillStyle = '#E2E8F0';
        ctx.textAlign = 'center';
        const label = node.display_name?.length > 20
          ? node.display_name.slice(0, 18) + '...'
          : node.display_name || '';
        ctx.fillText(label, node.x, node.y + node.radius + 14);
      }

      // Tooltip
      if (hoveredNode) {
        const hNode = nodes.find(n => n.id === hoveredNode);
        if (hNode) {
          const padding = 8;
          const text = `${hNode.display_name} (${hNode.entity_type})`;
          ctx.font = '12px sans-serif';
          const metrics = ctx.measureText(text);
          const tooltipW = metrics.width + padding * 2;
          const tooltipH = 24;
          const tx = Math.min(hNode.x + 20, width - tooltipW - 10);
          const ty = hNode.y - 30;

          ctx.fillStyle = 'rgba(15, 23, 42, 0.95)';
          ctx.strokeStyle = 'rgba(148, 163, 184, 0.3)';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.roundRect(tx, ty, tooltipW, tooltipH, 4);
          ctx.fill();
          ctx.stroke();

          ctx.fillStyle = '#E2E8F0';
          ctx.textAlign = 'left';
          ctx.fillText(text, tx + padding, ty + 16);
        }
      }
    }

    function tick() {
      simulate();
      draw();
      animRef.current = requestAnimationFrame(tick);
    }

    tick();

    // Mouse interaction
    function getNodeAt(mx, my) {
      for (let i = nodes.length - 1; i >= 0; i--) {
        const dx = mx - nodes[i].x;
        const dy = my - nodes[i].y;
        if (dx * dx + dy * dy < nodes[i].radius * nodes[i].radius) {
          return nodes[i];
        }
      }
      return null;
    }

    function onMouseMove(e) {
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const node = getNodeAt(mx, my);
      setHoveredNode(node ? node.id : null);
      canvas.style.cursor = node ? 'pointer' : 'default';

      if (dragNode) {
        dragNode.x = mx;
        dragNode.y = my;
        dragNode.vx = 0;
        dragNode.vy = 0;
      }
    }

    function onMouseDown(e) {
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const node = getNodeAt(mx, my);
      if (node) {
        setDragNode(node);
      }
    }

    function onMouseUp() {
      setDragNode(null);
    }

    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('mouseleave', onMouseUp);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('mouseleave', onMouseUp);
    };
  }, [graphData, hoveredNode, dragNode]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-virgo-teal-400"></div>
      </div>
    );
  }

  if (!graphData || graphData.nodes.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-gray-500">No graph data. Run enrichment to discover connections.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Controls */}
      <div className="flex items-center gap-4 px-6 py-3 bg-virgo-navy-800/50 border-b border-virgo-navy-600">
        <label className="text-sm text-gray-400">Depth:</label>
        <input
          type="range"
          min={1}
          max={4}
          value={depth}
          onChange={(e) => setDepth(parseInt(e.target.value))}
          className="w-32"
        />
        <span className="text-sm text-white">{depth} hops</span>
        <span className="text-sm text-gray-500 ml-4">
          {graphData.nodes.length} nodes, {graphData.edges.length} edges
        </span>

        {/* Legend */}
        <div className="flex items-center gap-3 ml-auto">
          {Object.entries(NODE_COLORS).map(([type, color]) => (
            <div key={type} className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }}></div>
              <span className="text-xs text-gray-400">{type}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 relative" style={{ minHeight: '600px' }}>
        <canvas
          ref={canvasRef}
          className="w-full h-full"
          style={{ background: 'transparent' }}
        />
      </div>
    </div>
  );
};

export default GraphVisualization;
